import { useCallback, useEffect, useState } from "react";
import { api } from "../../api/client";
import type { CustomerCard, CustomerRow, CustomersResponse } from "../../api/types";
import { useSession } from "../../state/session";
import { useToasts } from "../../state/toasts";
import { Empty, Modal, StatusChip } from "../../ui/components";
import { dateTime, money } from "../../ui/format";

/**
 * Клиенты (п. 27 ТЗ).
 *
 * Регистрации в системе нет: клиент — это телефон, а всё остальное о нём
 * выводится из заказов. Раздел отвечает на два вопроса: кто приходит снова и
 * кому можно писать.
 */
export function CustomersPage() {
  const { can } = useSession();
  const { run, notify } = useToasts();

  const [data, setData] = useState<CustomersResponse | null>(null);
  const [search, setSearch] = useState("");
  const [marketingOnly, setMarketingOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [card, setCard] = useState<CustomerCard | null>(null);

  const manage = can("customers.manage");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      if (marketingOnly) params.set("marketing", "true");

      setData(await api.get<CustomersResponse>(`/api/admin/customers?${params.toString()}`));
    } catch (error) {
      notify(error instanceof Error ? error.message : "Не удалось загрузить клиентов", "error");
    } finally {
      setLoading(false);
    }
  }, [search, marketingOnly, notify]);

  useEffect(() => {
    // Поиск не дёргает сервер на каждую букву: список читают глазами,
    // а не печатают в него вслепую.
    const timer = setTimeout(() => void load(), 300);
    return () => clearTimeout(timer);
  }, [load]);

  async function openCard(id: string) {
    try {
      setCard(await api.get<CustomerCard>(`/api/admin/customers/${id}`));
    } catch (error) {
      notify(error instanceof Error ? error.message : "Не удалось открыть карточку", "error");
    }
  }

  async function toggleMarketing(customer: CustomerRow) {
    const ok = await run(
      () => api.patch(`/api/admin/customers/${customer.id}`, { marketingOptIn: !customer.marketingOptIn }),
      customer.marketingOptIn ? "Согласие отозвано" : "Согласие отмечено"
    );
    if (ok) await load();
  }

  return (
    <>
      <div className="page-head">
        <div>
          <p className="eyebrow">База гостей</p>
          <h1>Клиенты</h1>
        </div>
        <a className="btn btn-sm" href="/api/admin/customers/export.csv" download>
          Выгрузить для рассылки
        </a>
      </div>

      {data ? (
        <section className="metrics">
          <div className="metric">
            <b>{data.totals.customers}</b>
            <span>клиентов в списке</span>
          </div>
          <div className="metric">
            <b>{data.totals.repeat}</b>
            <span>вернулись снова</span>
          </div>
          <div className="metric">
            <b>{data.totals.withMarketing}</b>
            <span>согласны на рассылку</span>
          </div>
          <div className="metric">
            <b>{money(data.totals.revenue)}</b>
            <span>принесли всего</span>
          </div>
        </section>
      ) : null}

      <section className="panel">
        <header>
          <div>
            <h2>Справочник</h2>
            <p>
              Клиент опознаётся по телефону: «+7 900 123-45-67» и «89001234567» — один человек с одной историей.
              Искать можно по имени или по любым цифрам номера.
            </p>
          </div>
        </header>

        <div className="filters">
          <label className="field">
            <span>Поиск</span>
            <input
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Имя или телефон"
            />
          </label>
          <label className="inline-check">
            <input
              type="checkbox"
              checked={marketingOnly}
              onChange={event => setMarketingOnly(event.target.checked)}
            />
            Только согласные на рассылку
          </label>
        </div>

        {loading && !data ? (
          <Empty>Загрузка…</Empty>
        ) : !data || data.items.length === 0 ? (
          <Empty>Никого не нашлось.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Клиент</th>
                  <th className="num">Заказов</th>
                  <th className="num">Принёс</th>
                  <th className="num">Средний чек</th>
                  <th>Последний заказ</th>
                  <th>Рассылка</th>
                  <th>Карточка</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map(customer => (
                  <tr key={customer.id}>
                    <td>
                      <strong>{customer.name}</strong>
                      <br />
                      <a href={`tel:${customer.phone}`}>{customer.phoneFormatted}</a>
                      {customer.comment ? (
                        <>
                          <br />
                          <small className="muted">{customer.comment}</small>
                        </>
                      ) : null}
                    </td>
                    <td className="num">
                      {customer.paidOrders}
                      {customer.cancelled > 0 ? (
                        <>
                          <br />
                          <small className="muted">отменено: {customer.cancelled}</small>
                        </>
                      ) : null}
                    </td>
                    <td className="num">{money(customer.revenue)}</td>
                    <td className="num">{money(customer.averageCheck)}</td>
                    <td>{dateTime(customer.lastOrderAt)}</td>
                    <td>
                      {manage ? (
                        <button
                          type="button"
                          className={`btn btn-sm ${customer.marketingOptIn ? "btn-primary" : ""}`}
                          onClick={() => void toggleMarketing(customer)}
                        >
                          {customer.marketingOptIn ? "Согласен" : "Нет согласия"}
                        </button>
                      ) : (
                        <span className="muted">{customer.marketingOptIn ? "согласен" : "нет"}</span>
                      )}
                    </td>
                    <td>
                      <button type="button" className="btn btn-sm" onClick={() => void openCard(customer.id)}>
                        Открыть
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {card ? (
        <CustomerModal
          card={card}
          manage={manage}
          onClose={() => setCard(null)}
          onSaved={async () => {
            setCard(null);
            await load();
          }}
        />
      ) : null}
    </>
  );
}

function CustomerModal({
  card,
  manage,
  onClose,
  onSaved
}: {
  card: CustomerCard;
  manage: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { run } = useToasts();

  const [name, setName] = useState(card.name);
  const [comment, setComment] = useState(card.comment);
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    const ok = await run(
      () => api.patch(`/api/admin/customers/${card.id}`, { name: name.trim(), comment: comment.trim() }),
      "Карточка сохранена"
    );
    setBusy(false);
    if (ok) await onSaved();
  }

  return (
    <Modal
      title={`${card.name} · ${card.phoneFormatted}`}
      onClose={onClose}
      actions={
        manage ? (
          <>
            <button type="button" className="btn" onClick={onClose}>
              Закрыть
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!name.trim() || busy}
              onClick={() => void save()}
            >
              {busy ? "Сохраняем…" : "Сохранить"}
            </button>
          </>
        ) : (
          <button type="button" className="btn" onClick={onClose}>
            Закрыть
          </button>
        )
      }
    >
      <section className="metrics">
        <div className="metric">
          <b>{card.paidOrders}</b>
          <span>оплаченных заказов</span>
        </div>
        <div className="metric">
          <b>{money(card.revenue)}</b>
          <span>принёс</span>
        </div>
        <div className="metric">
          <b>{money(card.averageCheck)}</b>
          <span>средний чек</span>
        </div>
        <div className="metric">
          <b>{dateTime(card.firstOrderAt)}</b>
          <span>первый заказ</span>
        </div>
      </section>

      {card.favourites.length > 0 ? (
        <p className="hint">
          Чаще всего берёт: {card.favourites.map(item => `${item.title} (${item.quantity})`).join(", ")}.
        </p>
      ) : null}

      <label className="field">
        <span>Имя</span>
        <input value={name} disabled={!manage} onChange={event => setName(event.target.value)} maxLength={120} />
      </label>

      <label className="field">
        <span>Заметка для сотрудников</span>
        <input
          value={comment}
          disabled={!manage}
          onChange={event => setComment(event.target.value)}
          maxLength={1000}
          placeholder="Например: просит без лука"
        />
      </label>

      <p className="hint">Заметку видят только сотрудники — клиенту она не показывается.</p>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Заказ</th>
              <th>Когда</th>
              <th>Статус</th>
              <th className="num">Сумма</th>
            </tr>
          </thead>
          <tbody>
            {card.history.map(order => (
              <tr key={order.id}>
                <td>
                  <strong>№{order.number}</strong>
                  <br />
                  <small className="muted">лавочка {order.benchId}</small>
                </td>
                <td>{dateTime(order.createdAt)}</td>
                <td>
                  <StatusChip status={order.status} />
                </td>
                <td className="num">
                  {money(order.total)}
                  {order.refundAmount > 0 ? (
                    <>
                      <br />
                      <small className="muted">возврат {money(order.refundAmount)}</small>
                    </>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}
